import React, { useEffect, useState, useRef, useMemo } from 'react';
import styles from './styles';
import { getEmptyReceivingSchedule, SaveApiResMessage } from '@/actions';
import { Icons, TerminalsIcon } from '@/assets';
import { ReactSVG } from 'react-svg';
import LoadingV2 from '@/components/Loading';
import timeAgo from '@/ctUtlis/timeAgo';
import DateSelector from './DateSelector';
import ShiftStatus from './ShiftStatus';
import { useStickyHeader } from '@/hooks/useStickyHeader';
import IconButton from '@/ctComponents/IconButton';
import ReceivablesHistoryOverlay from './ReceivablesHistoryOverlay';
import ReceivablesHistoryPanel from './ReceivablesHistoryPanel';
import IconButtonV2 from '@/ctComponents/IconButton/IconButtonV2';

// Terminal icon mapping for optimized image loading - created once outside component
const TERMINAL_ICON_MAP = {
  APM: TerminalsIcon.APM,
  FMS: TerminalsIcon.FMS,
  EVERPORT: TerminalsIcon.EVERPORT,
  LACT: TerminalsIcon.LACT,
  PCT: TerminalsIcon.PCT,
  PST: TerminalsIcon.PST,
  TRAPAC: TerminalsIcon.TRAPAC,
  WBCT: TerminalsIcon.WBCT,
  WCC: TerminalsIcon.WCC,
  YTI: TerminalsIcon.YTI,
  TTI: TerminalsIcon.TTI,
  ITS: TerminalsIcon.ITS,
  LBCT: TerminalsIcon.LBCT,
  MATSON: TerminalsIcon.MATSON,
  'SSA MARINE': TerminalsIcon.SSA,
  'SSA STL': TerminalsIcon.STL,
  PTS: TerminalsIcon.PTS,
};

const getTerminalIcon = (terminalName) => TERMINAL_ICON_MAP[terminalName] || '';

export default ({ TerminalList, DATADefault, submitFilter }) => {
  const [loading, setLoading] = React.useState(false);
  const [filteredData, setFilteredData] = useState(DATADefault);
  const [Terminals, setTerminals] = React.useState(TerminalList);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [sizeSelectOptions, setSizeSelectOptions] = useState(null);
  const [sslSelectOptions, setSslSelectOptions] = useState(null);
  const [selectedSIZE, setSelectedSize] = useState(null);
  const [selectedSSL, setSelectedSSL] = useState();
  const [selectedTab, setSelectedTab] = useState();
  const [historyOverlayOpen, setHistoryOverlayOpen] = useState(false);
  const [historyOverlayTerminalId, setHistoryOverlayTerminalId] =
    useState(null);

  const cls = styles();

  var today = new Date();
  var dd = String(today.getDate()).padStart(2, '0');
  var mm = String(today.getMonth() + 1).padStart(2, '0'); //January is 0!
  var yyyy = today.getFullYear();

  today = { month: mm, day: dd, year: yyyy };

  const tableRef = useRef(null);
  const nextColRef = useRef(null);
  const terminalColRef = useRef(null);
  const shiftColRef = useRef(null);
  const shiftRef1 = useRef(null);
  const shiftRef2 = useRef(null);
  const gateStatusRowRef = useRef(null);
  const nextAppointmentRowRef = useRef(null);
  const listContainerRef = useRef(null);

  const { theadRef: stickyTheadRef, tableRef: stickyTableRef } =
    useStickyHeader();

  useEffect(() => {
    const defaultObj =
      DATADefault != null && typeof DATADefault === 'object'
        ? DATADefault
        : {};
    const sslSelectOptions = Object.keys(defaultObj).map((i, index) => ({
      label: i,
      value: index + 1,
    }));
    setSslSelectOptions(sslSelectOptions);
    sslSelectOptions.unshift({ label: 'All', value: 0 });
    setSelectedSSL(sslSelectOptions[0]);
    const anlContainers = defaultObj?.ANL?.containers ?? {};
    const sizeSelectOptions = Object.keys(anlContainers).map((i, index) => ({
      label: i,
      value: index + 1,
    }));
    setSizeSelectOptions(sizeSelectOptions);
    sizeSelectOptions.unshift({ label: 'All', value: 0 });
    setSelectedSize(sizeSelectOptions[0]);
  }, [DATADefault]);

  useEffect(() => {
    setSelectedTab(`${today.year}-${today.month}-${today.day}`);
  }, []);

  // Ensure overflow is set to 'auto' on window resize if it's 'visible'
  useEffect(() => {
    const checkAndUpdateOverflow = () => {
      if (!listContainerRef.current) return;

      const container = listContainerRef.current;
      const computedStyle = window.getComputedStyle(container);
      const overflow = computedStyle.overflow;

      if (overflow === 'visible') {
        container.style.overflow = 'auto';
      }
    };

    // Check on mount
    checkAndUpdateOverflow();

    // Check on window resize
    window.addEventListener('resize', checkAndUpdateOverflow, {
      passive: true,
    });

    return () => {
      window.removeEventListener('resize', checkAndUpdateOverflow);
    };
  }, []);

  useEffect(() => {
    if (selectedDate && !submitFilter?.date) {
      const selectedDateFormatted = formatDate(selectedDate);
      changeTableDate(
        `${selectedDateFormatted.year}-${selectedDateFormatted.monthNumber}-${selectedDateFormatted.day}`
      );
      setSelectedTab(
        `${selectedDateFormatted.year}-${selectedDateFormatted.monthNumber}-${selectedDateFormatted.day}`
      );
    }
  }, [selectedDate, submitFilter?.date]);

  const changeTableDate = (date) => {
    handleChangeTable(date);
    setLoading(true);
    getEmptyReceivingSchedule(date)
      .then((res) => {
        setTerminals(res.data.restrictions);
        setLoading(false);
      })
      .catch((err) => {
        SaveApiResMessage({
          title: 'Error',
          message: err?.message || 'Failed to load data',
          mode: 'warning',
        });
        setLoading(false);
      });
  };

  const handleChangeTable = (index) => {
    setSelectedTab(index);
  };

  function getCurrentWeekDates(daydate) {
    // Adjust for Monday-based weeks: getDay() returns 0 for Sunday, 1 for Monday, etc.
    const currentDay = (daydate.getDay() + 6) % 7; // Adjust so Monday is 0 and Sunday is 6

    const firstDayOfWeek = new Date(daydate);
    firstDayOfWeek.setDate(daydate.getDate() - currentDay);

    const dates = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(firstDayOfWeek);
      date.setDate(firstDayOfWeek.getDate() + i);
      dates.push(date);
    }
    return dates;
  }

  // Example usage:
  const someDate = new Date(); // You can replace this with any date you want
  getCurrentWeekDates(someDate);

  // Format the date to separate day, abbreviated month, and year
  function formatDate(date, format) {
    const day = date.getDate().toString().padStart(2, '0');
    const dayNumber = date.getDate();
    const monthIndex = date.getMonth();
    const year = date.getFullYear();
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const monthNames = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ];
    const dayName = dayNames[date.getDay()];
    const monthNumber = String(monthIndex + 1).padStart(2, '0'); // Add leading zero if necessary
    const month = monthNames[monthIndex];
    if (format == 1) {
      return `${year}-${monthNumber}-${day}`;
    } else {
      return { day, dayNumber, dayName, monthNumber, month, year };
    }
  }
  // Print the dates of the current week
  const weekDates = getCurrentWeekDates(selectedDate);
  const formattedDates = weekDates.map((date) => formatDate(date));
  // const originalDate = "2024-04-20T04:31:52.000000Z";

  // Hide Gate Status and Next Appointment rows when thead is sticky
  useEffect(() => {
    if (!stickyTheadRef.current) return;

    let lastStickyState = null;
    let rafId = null;

    const checkStickyState = () => {
      const thead = stickyTheadRef.current;
      if (!thead) return;

      const computedStyle = window.getComputedStyle(thead);
      const position = computedStyle.position;
      const transform = computedStyle.transform;

      const hasStickyPosition = position === 'sticky';
      const hasTransform =
        transform !== 'none' &&
        transform !== '' &&
        transform !== 'translateY(0px)' &&
        transform !== 'matrix(1, 0, 0, 1, 0, 0)';

      // Check if thead is actually stuck by comparing its position
      const theadRect = thead.getBoundingClientRect();

      const isSticky =
        (hasStickyPosition && theadRect.top <= 100) || hasTransform;

      // Only update DOM if state changed
      if (lastStickyState === isSticky) return;
      lastStickyState = isSticky;

      if (gateStatusRowRef.current) {
        gateStatusRowRef.current.style.display = isSticky ? 'none' : '';
      }
      if (nextAppointmentRowRef.current) {
        nextAppointmentRowRef.current.style.display = isSticky ? 'none' : '';
      }

      // Add/remove bottom shadow when sticky
      if (thead) {
        if (isSticky) {
          thead.style.boxShadow =
            '0px 4px 6px -2px rgba(0, 0, 0, 0.1), 0px 2px 4px -1px rgba(0, 0, 0, 0.06)';
        } else {
          thead.style.boxShadow = '';
        }
      }
    };

    const throttledCheck = () => {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        checkStickyState();
        rafId = null;
      });
    };

    checkStickyState();

    const observer = new MutationObserver(() => {
      throttledCheck();
    });

    if (stickyTheadRef.current) {
      observer.observe(stickyTheadRef.current, {
        attributes: true,
        attributeFilter: ['style', 'class'],
        subtree: false,
      });
    }

    const mainScroll = document.getElementsByTagName('main')[0];
    if (mainScroll) {
      mainScroll.addEventListener('scroll', throttledCheck, { passive: true });
    }
    window.addEventListener('resize', throttledCheck, { passive: true });

    return () => {
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
      observer.disconnect();
      if (mainScroll) {
        mainScroll.removeEventListener('scroll', throttledCheck);
      }
      window.removeEventListener('resize', throttledCheck);
    };
  }, [stickyTheadRef]);

  const filterDataBySSL = (data, submitFilter) => {
    if (data == null || typeof data !== 'object') {
      return {};
    }
    const { ssl, size } = submitFilter || {};

    const filteredBySSL =
      !ssl || ssl.label === 'All'
        ? data
        : Object.keys(data).reduce((acc, key) => {
            if (key === ssl.label) {
              acc[key] = data[key];
            }
            return acc;
          }, {});

    // Then filter by Size within the filtered SSL data
    const filteredBySize =
      !size || size.label === 'All'
        ? filteredBySSL
        : Object.keys(filteredBySSL).reduce((acc, key) => {
            const containers = filteredBySSL[key]?.containers ?? {};
            acc[key] = {
              ...filteredBySSL[key],
              containers: Object.keys(containers)
                .filter((containerKey) => containerKey === size.label)
                .reduce((obj, containerKey) => {
                  obj[containerKey] = containers[containerKey];
                  return obj;
                }, {}),
            };
            return acc;
          }, {});

    return filteredBySize;
  };

  useEffect(() => {
    setLoading(true);
    const filteredDataa = filterDataBySSL(
      DATADefault ?? {},
      submitFilter ?? {}
    );
    setFilteredData(filteredDataa);

    // Update selectedDate and selectedTab when date filter is applied
    if (submitFilter?.date) {
      setSelectedDate(submitFilter.date);
      const selectedDateFormatted = formatDate(submitFilter.date);
      const newDateKey = `${selectedDateFormatted.year}-${selectedDateFormatted.monthNumber}-${selectedDateFormatted.day}`;
      setSelectedTab(newDateKey);

      // Load data for the new date
      handleChangeTable(newDateKey);
      getEmptyReceivingSchedule(newDateKey)
        .then((res) => {
          setTerminals(res.data.restrictions);
          setLoading(false);
        })
        .catch((err) => {
          SaveApiResMessage({
            title: 'Error',
            message: err?.message || 'Failed to load data',
            mode: 'warning',
          });
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, [DATADefault, submitFilter]);

  // Memoize terminal keys to avoid recalculating on every render
  const terminalKeys = useMemo(
    () => Object.keys(Terminals ?? {}),
    [Terminals]
  );

  // Memoize colSpan calculation
  const sslHeaderColSpan = useMemo(
    () => 2 + terminalKeys.length * 2,
    [terminalKeys.length]
  );

  // Memoize filtered data keys
  const filteredDataKeys = useMemo(
    () =>
      Object.keys(filteredData ?? {}).filter(
        (key) => key !== null && key !== undefined
      ),
    [filteredData]
  );

  // Extract inline styles to constants to avoid creating new objects on every render
  const flexStyle = { flex: 0.2 };

  // Helper function to get shift status className - avoids duplication and improves performance
  const getShiftStatusClassName = (shiftValue) => {
    if (shiftValue === true || shiftValue === 1) {
      return cls.openUser;
    }
    if (shiftValue === false || shiftValue === 0) {
      return cls.closeUser;
    }
    if (shiftValue === 'DUAL' || shiftValue === 2) {
      return cls.dualUser;
    }
    return cls.nullStatussUser;
  };

  // Memoize container keys for each SSL to avoid recalculating on every render
  const sslContainerKeysMap = useMemo(() => {
    const map = new Map();
    filteredDataKeys.forEach((key) => {
      const sslData = filteredData[key];
      if (sslData?.containers) {
        map.set(key, Object.keys(sslData.containers));
      }
    });
    return map;
  }, [filteredData, filteredDataKeys]);

  // Memoize shouldShowContainer check result
  const shouldShowAllContainers = useMemo(
    () => selectedSIZE == null || selectedSIZE.label === 'All',
    [selectedSIZE]
  );

  return (
    <>
      <div className="my-4 flex justify-between flex-wrap w-full gap-2 lg:gap-4">
        <DateSelector
          dates={formattedDates.map((date) => ({
            key: `${date.year}-${date.monthNumber}-${date.day}`,
            dayName: date.dayName,
            dayNumber: date.dayNumber,
            month: date.month,
          }))}
          selectedDate={selectedTab}
          onDateSelect={(dateKey) => changeTableDate(dateKey)}
          className=""
        />
        <div id="legendSection" className="flex gap-2 lg:gap-4 text-sm">
          <span className="flex flex-row items-center text-11">
            <ReactSVG src={Icons.tickICon} className="w-5 h-4.1" />
            Open for receiving
          </span>
          <span className="flex flex-row items-center text-11">
            <ReactSVG className="w-5 h-4.1 pb-0.5" src={Icons.close} />
            Closed for receiving
          </span>
          <span className="flex flex-row items-center -mt-0.5 text-11">
            <ReactSVG className="w-5 h-4.1 pt-0.5" src={Icons.transaction} />
            Dual transactions only
          </span>
          <span className="flex flex-row items-center -mt-1 text-11">
            <ReactSVG className="w-5 h-4.1 pt-0.5" src={Icons.message} />
            Hover for messages
          </span>
        </div>
      </div>
      <div ref={listContainerRef} className={`${cls.listContainerUser} mt-4`}>
        {/* Date Selector Component */}

        <table ref={tableRef} id="table" className="border-collapse">
          <thead className={cls.list} style={{ display: 'none' }}>
            <tr className={cls.tableRow}>
              <th ref={terminalColRef} scope="col" className="text-xs">
                Terminal
              </th>
              {terminalKeys.map((idx) => (
                <td
                  scope="col"
                  className=" min-w-24 w-[6%] p-0 text-xs py-1"
                  style={{ minWidth: '104.13px' }}>
                  <a
                    className="flex flex-col font-bold"
                    style={{ color: '#43424b' }}
                    target="_blank"
                    href={Terminals[idx].links?.restriction}
                    rel="noreferrer">
                    <img
                      alt={idx}
                      className="border-b"
                      width={'100%'}
                      src={getTerminalIcon(idx)}
                    />
                    <span className="min-h-[55px] h-max flex flex-col justify-center">
                      <span className="h-4 text-11">{idx}</span>
                    </span>
                  </a>
                  <span className="min-h-[55px] h-max flex flex-col justify-center">
                    <span className="h-4 text-11">
                      {Terminals[idx].pier ? Terminals[idx].pier : ' '}
                    </span>
                    <span className="h-4">
                      {Terminals[idx].blur_status === 1
                        ? 'No Data'
                        : Terminals[idx].restrictions_latest3?.updated_at
                        ? timeAgo(
                            Terminals[idx].restrictions_latest3?.updated_at
                          )
                        : ''}
                    </span>
                  </span>
                </td>
              ))}
            </tr>
            <tr className={cls.tableRow}>
              <th ref={shiftColRef} scope="col" className="text-xs">
                Shift Status
              </th>
              {terminalKeys.map((idx) => (
                <td className="min-w-20 w-[6%] p-0 text-xs">
                  <span className="flex items-center justify-around h-[42px] min-w-20 -mb-0">
                    <span
                      className="w-full flex justify-center h-12 w-fill-available items-center font-bold"
                      colSpan="1">
                      1st
                    </span>
                    <span
                      className="w-full flex justify-center h-12 w-fill-available items-center font-bold"
                      colSpan="1">
                      2nd
                    </span>
                  </span>
                </td>
              ))}
            </tr>
          </thead>
        </table>
        {loading ? (
          <div className="mt-16">
            <LoadingV2 />
          </div>
        ) : (
          <>
            <table
              className={`${cls.list} border-separate`}
              style={{ borderSpacing: 0 }}
              ref={stickyTableRef}>
              <thead ref={stickyTheadRef} className={cls.theadSticky}>
                <span></span>

                <tr
                  style={{ flex: 0.2 }}
                  ref={shiftRef1}
                  id=""
                  className={cls.tableHeaderRow}>
                  <th
                    // ref={terminalColRef}
                    scope="col"
                    className="text-xs border border-solid border-border-primary ">
                    Terminal
                  </th>
                  {terminalKeys.map((idx) => (
                    <td
                      scope="col"
                      className="min-w-20 w-[6%] p-0 text-xs py-1 border border-solid border-border-primary border-l-0 border-b-0">
                      <a
                        className="flex flex-col font-bold"
                        style={{ color: '#43424b' }}
                        target="_blank"
                        href={Terminals[idx].links?.restriction}
                        rel="noreferrer">
                        <img
                          alt={idx}
                          className="border-b"
                          width={'100%'}
                          src={getTerminalIcon(idx)}
                        />
                        <span className="min-h-[55px] h-max flex flex-col justify-center">
                          <span className="h-4 text-11 font-averta-bold">
                            {idx}
                          </span>
                        </span>
                      </a>
                      <span className="min-h-[55px] h-max flex flex-col justify-center">
                        <span className="h-4 text-11">
                          {Terminals[idx].pier ? Terminals[idx].pier : ' '}
                        </span>
                        <span className="h-4">
                          {Terminals[idx].blur_status === 1
                            ? 'No Data'
                            : Terminals[idx].restrictions_latest3?.updated_at
                            ? timeAgo(
                                Terminals[idx].restrictions_latest3?.updated_at
                              )
                            : ''}
                        </span>
                      </span>
                    </td>
                  ))}
                </tr>
                <tr
                  ref={gateStatusRowRef}
                  style={{ flex: 0.2 }}
                  className={cls.tableHeaderRow}>
                  <th className="text-xs border border-solid border-border-primary border-b-0 border-t-0">
                    Gate Status
                  </th>
                  {terminalKeys.map((idx) => (
                    <td
                      className={` border border-solid border-border-primary border-l-0 ${cls.tableTdUser}
                      `}>
                      <div className="flex items-center justify-around">
                        <span
                          className={`px-2 py-0.5 rounded-full text-10  ${
                            Terminals[idx].gate_status_shift1 === 1
                              ? 'text-success bg-success-light'
                              : Terminals[idx].gate_status_shift1 === 0
                              ? 'text-error-10 bg-error-light'
                              : 'text-black px-5'
                          }`}>
                          {Terminals[idx].gate_status_shift1 == 1
                            ? 'Open'
                            : Terminals[idx].gate_status_shift1 == 0
                            ? 'Closed'
                            : Terminals[idx].gate_status_shift1 == 2
                            ? 'Dual'
                            : ''}
                        </span>
                        <span
                          className={`px-2 py-0.5 rounded-full text-10  ${
                            Terminals[idx].gate_status_shift2 === 1
                              ? 'text-success bg-success-light'
                              : Terminals[idx].gate_status_shift2 === 0
                              ? 'text-error-10 bg-error-light'
                              : 'text-black px-5'
                          }`}>
                          {Terminals[idx].gate_status_shift2 == 1
                            ? 'Open'
                            : Terminals[idx].gate_status_shift2 == 0
                            ? 'Closed'
                            : Terminals[idx].gate_status_shift2 == 2
                            ? 'Dual'
                            : ''}
                        </span>
                      </div>
                    </td>
                  ))}
                </tr>
                <tr
                  ref={nextAppointmentRowRef}
                  style={{ flex: 0.2 }}
                  className={cls.tableHeaderRow}>
                  <th
                    ref={nextColRef}
                    className="text-xs border border-solid border-border-primary border-b-0">
                    History
                  </th>
                  {terminalKeys.map((idx) => (
                    <td
                      className={`border border-solid border-border-primary border-l-0 border-t-0 border-b-0 ${cls.tableTdUser}`}>
                      {/* <div className="flex items-center justify-center h-full"> */}
                        <IconButton
                          size="mini"
                          text="ScreenShots"
                          // icon="eye"
                          color="#43424b"
                          fontSize="12px"
                          style={{
                            height: '30px',
                            width: '90px',
                            padding: '0px',
                            gap: 'inherit',
                          }}
                          onClick={() => {
                            setHistoryOverlayTerminalId(Terminals[idx]?.id);
                            setHistoryOverlayOpen(true);
                          }}
                        />
                      {/* </div> */}
                    </td>
                  ))}
                </tr>
                <tr
                  style={{ flex: 0.2 }}
                  ref={shiftRef2}
                  className={cls.tableHeaderRow}>
                  <th className="text-xs border border-solid border-border-primary ">
                    Shift Status
                  </th>
                  {terminalKeys.map((idx) => (
                    <td className="min-w-20 w-[6%] p-0 text-xs border border-solid border-border-primary border-l-0 ">
                      <span className="flex items-center justify-around h-[42px] min-w-20 -mb-0">
                        <span
                          className="w-full flex justify-center h-12 w-fill-available items-center font-bold"
                          colSpan="1">
                          1st
                        </span>
                        <span
                          className="w-full flex justify-center h-12 w-fill-available items-center font-bold"
                          colSpan="1">
                          2nd
                        </span>
                      </span>
                    </td>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredDataKeys.map((key) => {
                  const sslData = filteredData[key];
                  const containerKeys = sslContainerKeysMap.get(key) || [];

                  return (
                    <React.Fragment key={key}>
                      {/* SSL Header Row */}
                      <tr className={cls.tableRow}>
                        <th
                          className={`border border-solid border-border-primary  border-t-0 ${cls.tableTdsslname}`}
                          colSpan={sslHeaderColSpan}>
                          {sslData.name}
                        </th>
                      </tr>
                      {/* Container Types Rows */}
                      {containerKeys.map((containerType) => {
                        const shouldShowContainer =
                          shouldShowAllContainers ||
                          selectedSIZE.label === containerType;

                        return (
                          <tr
                            key={`${key}-${containerType}`}
                            className={cls.tableRow}>
                            <th
                              className={`border border-solid border-border-primary border-t-0  ${cls.sslNamesThUser}`}>
                              {containerType}
                            </th>
                            {terminalKeys.map((idx) => {
                              const terminal = Terminals[idx];
                              const restrictionData =
                                terminal.restriction?.parsed?.[key];
                              const containerData =
                                restrictionData?.containers?.[containerType];
                              // Pre-compute className to avoid string concatenation in render
                              const tdClassName =
                                terminal.blur_status === 1
                                  ? cls.tableTdNull
                                  : `border border-solid border-border-primary border-l-0 border-t-0 ${cls.tableTdUser}`;
                              // Pre-compute shift status classNames when containerData exists
                              const shift1ClassName = containerData
                                ? `${getShiftStatusClassName(
                                    containerData.shift1
                                  )} w-48`
                                : null;
                              const shift2ClassName = containerData
                                ? `${getShiftStatusClassName(
                                    containerData.shift2
                                  )} w-48`
                                : null;

                              return (
                                <td
                                  key={`${key}-${containerType}-${idx}`}
                                  style={flexStyle}
                                  className={tdClassName}>
                                  {shouldShowContainer &&
                                    (terminal.blur_status === 1 ? (
                                      <tr className={cls.shiftStatusUser} />
                                    ) : !restrictionData ? (
                                      <tr className={cls.shiftStatusUser}>
                                        <span></span>
                                      </tr>
                                    ) : containerData ? (
                                      <tr className={cls.shiftStatusUser}>
                                        <span className={shift1ClassName}>
                                          <ShiftStatus
                                            shiftData={containerData}
                                            shiftType="shift1"
                                            terminalId={terminal.id}
                                            terminalSlug={terminal.slug}
                                            restrictionName={
                                              restrictionData.name
                                            }
                                            containerType={containerType}
                                            cls={cls}
                                            idx={idx}
                                            restrictionKey={key}
                                            selectedSIZE={selectedSIZE}
                                            selectedSSL={selectedSSL}
                                          />
                                        </span>
                                        <span className={shift2ClassName}>
                                          <ShiftStatus
                                            shiftData={containerData}
                                            shiftType="shift2"
                                            terminalId={terminal.id}
                                            terminalSlug={terminal.slug}
                                            restrictionName={
                                              restrictionData.name
                                            }
                                            containerType={containerType}
                                            cls={cls}
                                            idx={idx}
                                            restrictionKey={key}
                                            selectedSIZE={selectedSIZE}
                                            selectedSSL={selectedSSL}
                                          />
                                        </span>
                                      </tr>
                                    ) : null)}
                                </td>
                              );
                            })}
                          </tr>
                        );
                      })}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </>
        )}
      </div>
      <ReceivablesHistoryOverlay
        open={historyOverlayOpen}
        onClose={() => {
          setHistoryOverlayOpen(false);
          setHistoryOverlayTerminalId(null);
        }}>
        <ReceivablesHistoryPanel
          open={historyOverlayOpen}
          terminalId={historyOverlayTerminalId}
        />
      </ReceivablesHistoryOverlay>
    </>
  );
};
